"use client";

import { useMemo, useState } from "react";
import {
  Checkbox,
  CopyButton,
  ErrorNotice,
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  metreName,
  ScansionLines,
} from "@/components/tools/poetry/scansion-lines";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import {
  type FootId,
  MAX_FEET,
  type MeterChoice,
  scanPoem,
} from "@/lib/tool-kit/poetry/meter";
import { poemLines } from "@/lib/tool-kit/poetry/pronounce";
import { scansionText } from "@/lib/tool-kit/poetry/scansion-view";
import { POETRY_READING_STRINGS } from "@/lib/tool-kit/poetry/strings";
import { usePoetryDictionary } from "@/lib/tool-kit/poetry/use-dictionary";

const SHAKESPEARE = `Shall I compare thee to a summer’s day?
Thou art more lovely and more temperate:
Rough winds do shake the darling buds of May,
And summer’s lease hath all too short a date:`;

const BLAKE = `Tiger, tiger, burning bright
In the forests of the night,
What immortal hand or eye
Could frame thy fearful symmetry?`;

const TENNYSON = `Half a league, half a league,
Half a league onward,
All in the valley of Death
Rode the six hundred.`;

const DICKINSON = `Because I could not stop for Death,
He kindly stopped for me;
The carriage held but just ourselves
And Immortality.`;

/** English strings. A translated content file overrides these through `ui`. */
export const METER_ANALYZER_STRINGS = {
  ...POETRY_READING_STRINGS,
  textLabel: "Your poem — one line of verse per line",
  textPlaceholder: SHAKESPEARE,
  samplesLabel: "Try a sample:",
  sampleShakespeare: "Shakespeare, Sonnet 18",
  sampleBlake: "Blake, The Tiger",
  sampleTennyson: "Tennyson, The Charge of the Light Brigade",
  sampleDickinson: "Dickinson, Because I could not stop for Death",
  sampleShakespeareText: SHAKESPEARE,
  sampleBlakeText: BLAKE,
  sampleTennysonText: TENNYSON,
  sampleDickinsonText: DICKINSON,
  sampleCredit:
    "Samples are public-domain texts: Shakespeare’s Sonnets (1609), Blake’s Songs of Experience (1794), Tennyson’s Maud and Other Poems (1855), Dickinson’s Poems (1890).",
  clear: "Clear",
  footLabel: "Foot",
  footAuto: "Detect automatically",
  feetLabel: "Feet per line",
  feetAuto: "Detect automatically",
  alternatesLabel:
    "Allow alternative pronunciations (fire as one syllable, heaven as heav’n…)",
  emptyPrompt: "Type or paste a poem to scan it line by line.",
  heading: "Scansion",
  headlineRegular: "Dominant metre: {metre}",
  headlineLoose: "Loosest fit: {metre}",
  headlineNone: "No regular metre found — the closest fit is {metre}.",
  headlineForced: "Scanned as {metre}",
  statMetre: "Metre",
  statFit: "Average fit",
  statLines: "Lines fitting",
  statConfidence: "Confidence",
  linesFitting: "{fitting} of {total}",
  lineLengths: "Line lengths in feet: {lengths}",
  patternAlternating:
    "Alternating {long} and {short} feet, as in ballad and hymn metre.",
  patternUniform: "Every line has about the same length.",
  patternVaried: "The line lengths vary.",
  lowEvidence:
    "Most syllables here are one-syllable words, which carry no fixed stress, so the dictionary gives little to go on. Treat the metre as a suggestion.",
  rankingHeading: "How each foot type fits",
  rankingNote:
    "Average fit over the whole poem, each line at its best length. Iambic and trochaic readings of the same line differ only by a one-syllable shift, so both can score well.",
  rankingFoot: "{foot} ({fit})",
  fitPercent: "{percent}% fit",
  fitLabels: {
    regular: "Regular",
    substitutions: "Regular with substitutions",
    loose: "Loose fit",
    irregular: "Does not fit",
  },
  confidenceLine: "Confidence: {level}",
  confidenceLevels: { high: "high", medium: "medium", low: "low" },
  footNames: {
    iamb: "iamb",
    trochee: "trochee",
    spondee: "spondee",
    pyrrhic: "pyrrhic",
    anapest: "anapest",
    dactyl: "dactyl",
    amphibrach: "amphibrach",
    stress: "single stress",
    unstressed: "single unstressed",
    other: "irregular foot",
  },
  footAdjectives: {
    iamb: "iambic",
    trochee: "trochaic",
    anapest: "anapestic",
    dactyl: "dactylic",
  },
  lengthNames: [
    "monometer",
    "dimeter",
    "trimeter",
    "tetrameter",
    "pentameter",
    "hexameter",
    "heptameter",
    "octameter",
  ],
  metreName: "{foot} {length}",
  variants: {
    regular: "",
    feminine: "Feminine ending: an extra unstressed syllable closes the line.",
    catalectic: "Catalectic: the last foot is cut short.",
    headless:
      "Headless: the line starts on a stress, without its opening weak syllable.",
    truncated: "Truncated: the line ends on a single stress.",
  },
  substitution: "substitution",
  feetRowLabel: "Feet",
  linesHeading: "Line by line",
  lineHeading: "Line {number}",
  marksLabel: "Stress marks for each word",
  legend:
    "/ stressed · ˘ unstressed · × flexible one-syllable word · ? stress unknown. Rose marks a syllable against the pattern; amber a stressed one-syllable word placed on a weak beat.",
  noteAlternate:
    "one:Read “{word}” as {count} syllable to fit the metre.|other:Read “{word}” as {count} syllables to fit the metre.",
  noteExtra:
    "one:{count} syllable fits no beat.|other:{count} syllables fit no beat.",
  noteMissing:
    "one:The pattern has {count} beat the line does not fill.|other:The pattern has {count} beats the line does not fill.",
  noteRival:
    "Also reads as {metre} within a point — scansion is a judgment call.",
  linesScanned: "one:{count} line scanned.|other:{count} lines scanned.",
  copyScansion: "Copy scansion",
  copied: "Copied!",
  methodNote:
    "Stress comes from the CMU Pronouncing Dictionary. One-syllable function words (the, of, to, and…) are treated as flexible because speakers stress them or not as the line needs; other one-syllable words keep their stress but may sit on a weak beat at a small cost.",
} as const;

type Strings = typeof METER_ANALYZER_STRINGS;

const ID = "meter";
const FOOTS: readonly FootId[] = ["iamb", "trochee", "anapest", "dactyl"];

function footOptions(strings: Strings) {
  return [
    { value: "auto", label: strings.footAuto },
    ...FOOTS.map((foot) => ({
      value: foot,
      label: strings.footAdjectives[foot],
    })),
  ];
}

function feetOptions(strings: Strings) {
  return [
    { value: "auto", label: strings.feetAuto },
    ...Array.from({ length: MAX_FEET }, (_, k) => ({
      value: String(k + 1),
      label: strings.lengthNames[k],
    })),
  ];
}

function toFoot(value: string): FootId | undefined {
  return FOOTS.find((foot) => foot === value);
}

export default function MeterAnalyzerWidget() {
  const strings = useToolUi(METER_ANALYZER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const [foot, setFoot] = useState("auto");
  const [feet, setFeet] = useState("auto");
  const [alternates, setAlternates] = useState(true);
  const hasText = text.trim().length > 0;
  const { lookup, status } = usePoetryDictionary(hasText);

  const choice: MeterChoice = useMemo(
    () => ({
      foot: toFoot(foot),
      feet: feet === "auto" ? undefined : Number(feet),
    }),
    [foot, feet]
  );
  const lines = useMemo(() => poemLines(text, lookup), [text, lookup]);
  const scansion = useMemo(
    () => (hasText ? scanPoem(lines, choice, alternates) : null),
    [hasText, lines, choice, alternates]
  );

  const samples = [
    [strings.sampleShakespeare, strings.sampleShakespeareText],
    [strings.sampleBlake, strings.sampleBlakeText],
    [strings.sampleTennyson, strings.sampleTennysonText],
    [strings.sampleDickinson, strings.sampleDickinsonText],
  ] as const;

  let headline = "";
  let metre = "";
  if (scansion) {
    const forced = choice.foot !== undefined || choice.feet !== undefined;
    metre = metreName(strings, scansion.foot, scansion.feet);
    const template = forced
      ? strings.headlineForced
      : {
          regular: strings.headlineRegular,
          loose: strings.headlineLoose,
          none: strings.headlineNone,
        }[scansion.regularity];
    headline = t(template, { metre });
  }

  let patternText = "";
  if (scansion) {
    if (scansion.pattern === "alternating" && scansion.alternating) {
      patternText = t(strings.patternAlternating, {
        long: fmt(scansion.alternating[0]),
        short: fmt(scansion.alternating[1]),
      });
    } else {
      patternText =
        scansion.pattern === "uniform"
          ? strings.patternUniform
          : strings.patternVaried;
    }
  }

  return (
    <>
      <TextAreaField
        id={`${ID}-text`}
        label={strings.textLabel}
        onChange={setText}
        placeholder={strings.textPlaceholder}
        prose
        rows={7}
        value={text}
      />
      <div className="flex flex-wrap items-center gap-2">
        <span className={HINT}>{strings.samplesLabel}</span>
        {samples.map(([label, body]) => (
          <button
            className={BUTTON_SECONDARY}
            key={label}
            onClick={() => setText(body)}
            type="button"
          >
            {label}
          </button>
        ))}
        <button
          className={BUTTON_SECONDARY}
          disabled={!hasText}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <SelectField
          id={`${ID}-foot`}
          label={strings.footLabel}
          onChange={setFoot}
          options={footOptions(strings)}
          value={foot}
        />
        <SelectField
          id={`${ID}-feet`}
          label={strings.feetLabel}
          onChange={setFeet}
          options={feetOptions(strings)}
          value={feet}
        />
      </div>
      <Checkbox
        checked={alternates}
        label={strings.alternatesLabel}
        onChange={setAlternates}
      />
      {status === "loading" ? (
        <Notice>{strings.dictionaryLoading}</Notice>
      ) : null}
      {status === "failed" ? (
        <ErrorNotice>{strings.dictionaryFailed}</ErrorNotice>
      ) : null}

      {scansion ? (
        <section aria-live="polite" className="space-y-5">
          <h2 className={WIDGET_H2}>{strings.heading}</h2>
          <div className={RESULT_CARD}>
            <p className="font-semibold text-foreground">{headline}</p>
            <p className="mt-1 text-muted-foreground text-sm">{patternText}</p>
            <p className="mt-1 text-muted-foreground text-xs">
              {t(strings.lineLengths, {
                lengths: scansion.lengths.map((n) => fmt(n)).join(", "),
              })}
            </p>
          </div>
          <StatGrid>
            <StatCard emphasis label={strings.statMetre} value={metre} />
            <StatCard
              label={strings.statFit}
              value={`${fmt(Math.round(scansion.score * 100))}%`}
            />
            <StatCard
              label={strings.statLines}
              value={t(strings.linesFitting, {
                fitting: fmt(scansion.fitting),
                total: fmt(scansion.lines.length),
              })}
            />
            <StatCard
              label={strings.statConfidence}
              value={strings.confidenceLevels[scansion.confidence]}
            />
          </StatGrid>
          {scansion.fixedShare < 0.2 ? (
            <Notice>{strings.lowEvidence}</Notice>
          ) : null}
          <ScansionLines lines={scansion.lines} strings={strings} />
          <section className="space-y-2">
            <h3 className={WIDGET_H3}>{strings.rankingHeading}</h3>
            <p className={HINT}>{strings.rankingNote}</p>
            <p className="text-foreground text-sm">
              {scansion.ranking
                .map((entry) =>
                  t(strings.rankingFoot, {
                    foot: strings.footAdjectives[entry.foot],
                    fit: `${fmt(Math.round((1 - entry.perSyllable) * 100))}%`,
                  })
                )
                .join(" · ")}
            </p>
          </section>
          <p className={HINT}>{strings.methodNote}</p>
          <p className={HINT}>
            {tPlural(strings.linesScanned, locale, scansion.lines.length, {
              count: fmt(scansion.lines.length),
            })}
          </p>
          <CopyButton
            copiedLabel={strings.copied}
            label={strings.copyScansion}
            text={scansionText(scansion.lines)}
          />
          <p className={HINT}>{strings.sampleCredit}</p>
        </section>
      ) : (
        <Notice>{strings.emptyPrompt}</Notice>
      )}
    </>
  );
}
